"use client";

import {usePathname} from "next/navigation";
import React, {useState} from "react";
import {motion, useReducedMotion} from "motion/react";
import useScreenSize from "@/hooks/useScreenSize";
import {useTranslation} from "react-i18next";
import {ChatInput} from "@/components/chat";
import BrandImage from "@/components/common/BrandImage";
import Button from "@/components/library/Button";
import {useChatTransfer} from "@/contexts/common/ChatTransferContextProvider";
import {useAlert} from "@/hooks/useAlert";
import {useChat} from "@/hooks/useChat";
import {usePermissions} from "@/hooks/usePermissions";
import {Permissions, RoleData} from "@/enums/user";
import {useSkills} from "@/hooks/useSkills";
import {useChatSkills} from "@/components/chat/useChatSkills";
import {useWingmanRouting} from "@/hooks/useWingmanRouting";
import {captureWingmanConversationFlow} from "@/lib/wingman-tracing";
import {pinConversationRouting, resolveWingmanRouting} from "@/services/wingman";
import {pickGreetingKey} from "@/utils/chat";
import {workflowBuilderRouteId} from "@/utils/common/constants/routes";
import DashRippleCanvas, {DASH_RIPPLE_REVEAL_TRANSITION} from "./DashRippleCanvas";

/** Creates a conversation in place and transfers its first message to the chat. */
type GailConversationWelcomeProps = {
   orgId?: string;
   onConversationCreated: (id: string) => void;
   /** `sheet` is the corner panel: a short prompt and a floating composer. */
   presentation?: "greeting" | "sheet";
};

function SheetWelcome({composer}: {composer: React.ReactNode}) {
   const {t} = useTranslation("ask");
   const {isMobile} = useScreenSize();
   const reducedMotion = useReducedMotion();
   // The prompt fades in behind each ripple and hides, unseen, once the sheet is fully closed.
   const [rippleRevealed, setRippleRevealed] = useState(false);

   return (
      <div className="flex h-full flex-col pb-4">
         <div className="relative isolate flex min-h-0 flex-1 items-center justify-center overflow-hidden px-6 text-center">
            <DashRippleCanvas
               onRippleStart={() => setRippleRevealed(true)}
               onRippleReset={() => setRippleRevealed(false)}
            />
            <motion.div
               initial={false}
               animate={{opacity: reducedMotion || rippleRevealed ? 1 : 0}}
               transition={rippleRevealed ? DASH_RIPPLE_REVEAL_TRANSITION : {duration: 0}}
               className="relative flex flex-col items-center gap-1.5"
            >
               <BrandImage variant="small" height={24} />
               <p className="mt-1 text-sm text-700">{t("launcher.start")}</p>
               {!isMobile && (
                  <p className="max-w-[20rem] text-xs leading-5 text-600">{t("launcher.resizeTip")}</p>
               )}
            </motion.div>
         </div>
         <div className="flex-shrink-0 px-4 pb-2">{composer}</div>
         <p className="shrink-0 px-4 text-center text-xs text-600">{t("chatDisclaimer")}</p>
      </div>
   );
}

const GailConversationWelcome: React.FC<GailConversationWelcomeProps> = ({
   orgId,
   onConversationCreated,
   presentation = "greeting",
}) => {
   const {t} = useTranslation("ask");
   const {t: at} = useTranslation("assistants");
   const {setAlert} = useAlert();
   const {setOptimisticTransfer} = useChatTransfer();
   const builderWorkflowId = workflowBuilderRouteId(usePathname() ?? "");
   const {canAccess} = usePermissions();
   const {skills} = useSkills(canAccess(RoleData.Assistants, Permissions.Read));
   const displaySkills = useChatSkills(skills);
   const [isCreating, setIsCreating] = useState(false);
   // Same randomized time-of-day greeting as the full GailGPT welcome page.
   // Lazy state so re-renders don't re-roll it; this welcome only mounts
   // client-side, so there's no SSR/hydration concern here.
   const [greetingKey] = useState(pickGreetingKey);

   const {
      inputValue,
      setInputValue,
      selectedAssistant,
      isLoadingAssistants,
      displayAssistants,
      displaySelectedAssistant,
      handleAssistantChange,
      handleFilesSelected,
      handleFilesChange,
      attachedFiles,
      fileUploadConfig,
      getAuthenticatedClient,
      fetchAssistants,
      hasFetchError,
   } = useChat({orgId, isNewConversation: true});
   const flaggedRouting = useWingmanRouting(selectedAssistant?.model);
   // Workflow tools exist only on Wingman v2, so the builder's conversation always starts there.
   const wingmanRouting = builderWorkflowId ? resolveWingmanRouting(true, selectedAssistant?.model) : flaggedRouting;

   const hasNoAssistants =
      !isLoadingAssistants &&
      !hasFetchError &&
      (!displayAssistants || displayAssistants.length === 0);

   const canCreate = !isCreating && !!selectedAssistant && selectedAssistant.id !== "loading" && !!orgId;

   const startConversation = async (textToSend: string, files: File[], assistantId: string): Promise<void> => {
      if (!orgId) return;
      setIsCreating(true);
      try {
         const client = await getAuthenticatedClient(wingmanRouting);
         const conversation = await client.conversations.createConversation({
            xOrgId: orgId,
            createConversationRequest: {
               assistantId,
               type: "gpt",
            },
         });
         if (!conversation.conversationId) {
            throw new Error("Failed to create conversation");
         }
         const routing = pinConversationRouting(conversation.conversationId, wingmanRouting);
         captureWingmanConversationFlow({
            action: "create",
            conversationId: conversation.conversationId,
            routing,
         });

         const fileData = await Promise.all(
            files.map(async (file) => ({
               name: file.name,
               type: file.type,
               size: file.size,
               data: Buffer.from(await file.arrayBuffer()).toString("base64"),
            }))
         );

         setOptimisticTransfer(conversation.conversationId, {
            message: {text: textToSend, fileData, assistantId},
            assistantId,
            orgId,
         });

         onConversationCreated(conversation.conversationId);
      } catch (error) {
         console.error("Error creating conversation:", error);
         setAlert({
            status: "error",
            message: t("creatingConversationFailed"),
            secondsToClose: 10,
         });
      } finally {
         setIsCreating(false);
      }
   };

   const handleSend = async (messageText?: string) => {
      const textToSend = (messageText ?? inputValue).trim();
      if ((!textToSend && attachedFiles.length === 0) || !canCreate || !selectedAssistant) return;
      await startConversation(textToSend, attachedFiles, selectedAssistant.id);
   };

   if (hasFetchError) {
      return (
         <div className="flex flex-col items-center justify-center h-full gap-4 px-6 text-center">
            <p className="text-sm text-700">{at("fetchError.description")}</p>
            <Button onClick={() => fetchAssistants()}>{at("fetchError.retry")}</Button>
         </div>
      );
   }

   if (hasNoAssistants) {
      return (
         <div className="flex flex-col items-center justify-center h-full gap-2 px-6 text-center">
            <h3 className="font-semibold text-base text-900">{at("noAssistants")}</h3>
            <p className="text-sm text-600">{at("contactAdmin.description")}</p>
         </div>
      );
   }

   let placeholder = t("askMeAnything");
   if (isCreating) {
      placeholder = t("startingConversation");
   } else if (isLoadingAssistants) {
      placeholder = t("loadingAssistants");
   }

   const composer = (
      <ChatInput
         skills={displaySkills}
         isLoadingSkills={skills === null}
         value={inputValue}
         onChange={setInputValue}
         onSubmit={handleSend}
         disabled={isLoadingAssistants || isCreating}
         isSubmitting={isCreating}
         loading={isCreating}
         placeholder={
            presentation === "sheet" && !isCreating && !isLoadingAssistants
               ? t("launcher.placeholder")
               : placeholder
         }
         variant="compact"
         assistants={displayAssistants}
         selectedAssistant={displaySelectedAssistant || undefined}
         onAssistantChange={isLoadingAssistants || isCreating ? () => {} : handleAssistantChange}
         showAssistantSelector
         onFilesSelected={handleFilesSelected}
         attachedFiles={attachedFiles}
         onFilesChange={handleFilesChange}
         fileUploadConfig={fileUploadConfig}
         fileCompatibilityTooltip={t("assistantDoesNotSupportFilesTooltip")}
         enableDictation
         enableMemory
      />
   );

   if (presentation === "sheet") {
      return (
         <SheetWelcome composer={composer} />
      );
   }

   return (
      <div className="flex flex-col h-full">
         <div className="flex-1 flex flex-col items-center justify-center gap-3 px-6 text-center min-h-0">
            <BrandImage variant="small" height={28} />
            {/* Same typography as the full welcome page greeting (Source
                Serif, normal weight), scaled down for the narrow panel. */}
            <h2 className="font-source-serif font-normal text-[26px] tracking-[-0.5px] text-900">
               {t(greetingKey)}
            </h2>
         </div>
         <div className="flex-shrink-0 p-3">{composer}</div>
      </div>
   );
};

export default GailConversationWelcome;
